// src/features/task/TaskContainerConfigsAdmin.tsx
// Admin: per-container task configuration (project, team, pipeline…). There is
// no list endpoint — a container config is looked up by its container entity,
// then created / edited / deleted. Wraps Get/GetByEntity/Create/Update/Delete
// ContainerConfig.

import { useState } from 'react';
import { observer } from '@react';
import { useTaskContainerConfig, useI18n } from '@react/hooks';
import { Button, Card, CardContent, CardHeader, CardTitle, CardDescription, Input, Alert } from '@react/components';
import { Select } from '@/components/Select';
import { PipelinePicker } from '@/components/PipelinePicker';
import { useScope } from '@/hooks/useScope';
import { SCOPE_ENTITY_TYPES, errMsg, makeEntity } from '@/features/task/panelHelpers';

interface OverrideRow {
  slug: string;
  pipelineId: string;
}

export const TaskContainerConfigsAdmin = observer(() => {
  const store = useTaskContainerConfig();
  const { t } = useI18n();
  const scope = useScope();

  const [containerType, setContainerType] = useState<string>(SCOPE_ENTITY_TYPES[0]);
  const [containerId, setContainerId] = useState('');
  const [looked, setLooked] = useState(false);
  const [config, setConfig] = useState<any | null>(null);
  const [allowed, setAllowed] = useState('');
  const [defaultType, setDefaultType] = useState('');
  const [keyPrefix, setKeyPrefix] = useState('');
  const [overrides, setOverrides] = useState<OverrideRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const applyConfig = (cfg: any | null) => {
    setConfig(cfg);
    setAllowed((cfg?.allowed_type_slugs || []).join(', '));
    setDefaultType(cfg?.default_type_slug || '');
    setKeyPrefix(cfg?.key_prefix || '');
    const overrideEntries = Object.entries(cfg?.pipeline_overrides || {}) as [string, string][];
    setOverrides(overrideEntries.map(([slug, pipelineId]) => ({ slug, pipelineId })));
  };

  const addOverrideRow = () => setOverrides((prev) => [...prev, { slug: '', pipelineId: '' }]);
  const removeOverrideRow = (index: number) => setOverrides((prev) => prev.filter((_, i) => i !== index));
  const updateOverrideRow = (index: number, patch: Partial<OverrideRow>) =>
    setOverrides((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  /** Build the pipeline_overrides map from rows with both a slug and a pipeline set. */
  const buildOverridesMap = (): Record<string, string> => {
    const map: Record<string, string> = {};
    for (const row of overrides) {
      const slug = row.slug.trim();
      if (slug && row.pipelineId) map[slug] = row.pipelineId;
    }
    return map;
  };

  const lookup = async () => {
    if (!containerId.trim()) return;
    setBusy(true); setError(null); setLooked(false);
    try {
      const res: any = await store.GetContainerConfigByEntity(containerType, containerId.trim());
      applyConfig(res?.config?.entity?.id ? res.config : null);
      setLooked(true);
    } catch (e) {
      setError(errMsg(e, t('task.containers.lookupError')));
    } finally {
      setBusy(false);
    }
  };

  const parseAllowed = () => allowed.split(',').map((s) => s.trim()).filter(Boolean);

  const create = async () => {
    setBusy(true); setError(null);
    try {
      const res: any = await store.CreateContainerConfig({
        entity: makeEntity('', 'task_container_config', scope),
        container: makeEntity(containerId.trim(), containerType, scope),
        allowed_type_slugs: parseAllowed(),
        default_type_slug: defaultType.trim() || undefined,
        key_prefix: keyPrefix.trim() || undefined,
        pipeline_overrides: buildOverridesMap(),
      } as any);
      applyConfig(res?.config || null);
    } catch (e) {
      setError(errMsg(e, t('task.containers.saveError')));
    } finally {
      setBusy(false);
    }
  };

  const update = async () => {
    const id = config?.entity?.id;
    if (!id) return;
    setBusy(true); setError(null);
    try {
      const res: any = await store.UpdateContainerConfig(id, {
        config: {
          ...config,
          allowed_type_slugs: parseAllowed(),
          default_type_slug: defaultType.trim(),
          key_prefix: keyPrefix.trim(),
          pipeline_overrides: buildOverridesMap(),
        },
        field_mask: ['allowed_type_slugs', 'default_type_slug', 'key_prefix', 'pipeline_overrides'],
      } as any);
      applyConfig(res?.config || null);
    } catch (e) {
      setError(errMsg(e, t('task.containers.saveError')));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const id = config?.entity?.id;
    if (!id) return;
    setBusy(true); setError(null);
    try {
      await store.DeleteContainerConfig(id);
      applyConfig(null);
    } catch (e) {
      setError(errMsg(e, t('task.containers.deleteError')));
    } finally {
      setBusy(false);
    }
  };

  const refetchById = async () => {
    const id = config?.entity?.id;
    if (!id) return;
    setBusy(true); setError(null);
    try {
      const res: any = await store.GetContainerConfig(id);
      applyConfig(res?.config || null);
    } catch (e) {
      setError(errMsg(e, t('task.containers.lookupError')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('task.containers.title')}</CardTitle>
        <CardDescription>{t('task.containers.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.containers.containerType')}</label>
            <Select value={containerType} onChange={(e) => setContainerType(e.target.value)}>
              {SCOPE_ENTITY_TYPES.map((ct) => <option key={ct} value={ct}>{ct}</option>)}
            </Select>
          </div>
          <Input
            label={t('task.containers.containerId')}
            value={containerId}
            onChange={(e) => setContainerId(e.target.value)}
            placeholder={t('task.containers.containerIdPlaceholder')}
            helperText={t('task.containers.containerIdHint')}
          />
          <Button variant="secondary" onClick={lookup} isLoading={busy} disabled={!containerId.trim()}>{t('task.containers.lookup')}</Button>
        </div>

        {looked && (
          <div className="space-y-3 rounded-md border border-border p-3">
            <p className="text-sm font-medium text-foreground">
              {config ? t('task.containers.existing') : t('task.containers.notConfigured')}
            </p>
            <Input label={t('task.containers.allowedTypes')} value={allowed} onChange={(e) => setAllowed(e.target.value)} placeholder={t('task.containers.allowedTypesPlaceholder')} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input label={t('task.containers.defaultType')} value={defaultType} onChange={(e) => setDefaultType(e.target.value)} />
              <Input label={t('task.containers.keyPrefix')} value={keyPrefix} onChange={(e) => setKeyPrefix(e.target.value.toUpperCase())} placeholder="OPS" />
            </div>

            <div className="space-y-2 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('task.containers.pipelineOverrides')}</span>
                <Button size="sm" variant="secondary" onClick={addOverrideRow}>{t('task.containers.addOverride')}</Button>
              </div>
              <p className="text-xs text-muted-foreground">{t('task.containers.pipelineOverridesHint')}</p>
              {overrides.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('task.containers.noOverrides')}</p>
              ) : (
                <div className="space-y-2">
                  {overrides.map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={row.slug}
                        onChange={(e) => updateOverrideRow(i, { slug: e.target.value })}
                        placeholder={t('task.containers.overrideTypeSlugPlaceholder')}
                        autoCapitalize="off"
                        className="w-40 rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      <PipelinePicker
                        value={row.pipelineId}
                        onChange={(id) => updateOverrideRow(i, { pipelineId: id })}
                        className="flex-1"
                      />
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeOverrideRow(i)}>
                        {t('task.containers.removeOverride')}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {config ? (
                <>
                  <Button size="sm" onClick={update} isLoading={busy}>{t('common.save')}</Button>
                  <Button size="sm" variant="secondary" onClick={refetchById} isLoading={busy}>{t('task.refresh')}</Button>
                  <Button size="sm" variant="danger" onClick={remove} isLoading={busy}>{t('common.delete')}</Button>
                </>
              ) : (
                <Button size="sm" onClick={create} isLoading={busy}>{t('task.containers.create')}</Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

export default TaskContainerConfigsAdmin;
